import { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '../services/supabase';

const USER_STORAGE_KEY = 'synaptek_user';

// Create the Auth Context
const AuthContext = createContext(null);

const mapSupabaseUser = (u) => (u ? { id: u.id, email: u.email } : null);

// Auth Provider component to wrap the app
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [authReady, setAuthReady] = useState(false);

    // Restore session from Supabase on mount and subscribe to auth changes
    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setUser(mapSupabaseUser(session?.user ?? null));
            setAuthReady(true);
        });

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setUser(mapSupabaseUser(session?.user ?? null));
        });

        return () => subscription.unsubscribe();
    }, []);

    useEffect(() => {
        if (user) {
            try {
                localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
            } catch (e) {
                // ignore
            }
        } else {
            localStorage.removeItem(USER_STORAGE_KEY);
        }
    }, [user]);

    const logout = async () => {
        await supabase.auth.signOut();
        setUser(null);
    };

    const value = { user, updateFn: setUser, logout, authReady };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Hook to use auth context
const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default useAuth;
